import type { AuthMeResponse, PermissionKey, SessionUser } from '@warehouse/shared';
import { SUPER_ADMIN_ROLE_KEY } from '@warehouse/shared';
import { create } from 'zustand';

/**
 * The session store: who is signed in and what chrome they may see.
 *
 * Permission checks here are for HIDING UI ONLY. The server re-checks every
 * request with `@Permissions(...)` (api-core: two global guards, everything
 * denied unless opted out), so a user who forges `permissions` in devtools gets
 * a prettier menu and exactly the same 403s. That split is deliberate — the old
 * WMS shipped permission keys the backend never enforced.
 *
 * `super_admin` bypasses in code on both ends and is never seeded into
 * `role_permissions`, so it must be checked as a ROLE, not a permission.
 */

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthSession {
  id: string;
  kind: 'web' | 'device';
  expiresAt: string;
}

export interface AuthState {
  status: AuthStatus;
  user: SessionUser | null;
  session: AuthSession | null;
  /** Set of `user.permissions`, for O(1) checks in render paths. */
  permissions: ReadonlySet<string>;

  setSession: (me: AuthMeResponse) => void;
  setUser: (user: SessionUser) => void;
  setAnonymous: () => void;
  setLoading: () => void;
}

const EMPTY_PERMISSIONS: ReadonlySet<string> = new Set<string>();

export const useAuthStore = create<AuthState>((set) => ({
  status: 'loading',
  user: null,
  session: null,
  permissions: EMPTY_PERMISSIONS,

  setSession: (me) =>
    set({
      status: 'authenticated',
      user: me.user,
      session: me.session,
      permissions: new Set(me.user.permissions),
    }),

  setUser: (user) =>
    set((state) => ({
      ...state,
      status: 'authenticated',
      user,
      permissions: new Set(user.permissions),
    })),

  setAnonymous: () =>
    set({ status: 'anonymous', user: null, session: null, permissions: EMPTY_PERMISSIONS }),

  setLoading: () => set((state) => ({ ...state, status: 'loading' })),
}));

// ---------------------------------------------------------------------------
// permission helpers (pure — usable outside React, e.g. in route loaders)
// ---------------------------------------------------------------------------

export function isSuperAdminUser(user: SessionUser | null): boolean {
  if (!user) return false;
  return user.isSuperAdmin || user.roleKeys.includes(SUPER_ADMIN_ROLE_KEY);
}

/** OR semantics — matches `@Permissions(a, b)` on the server. */
export function userHasPermission(
  user: SessionUser | null,
  permissions: ReadonlySet<string>,
  required: readonly PermissionKey[] | PermissionKey | undefined,
): boolean {
  if (!user) return false;
  if (required === undefined) return true;
  const keys = typeof required === 'string' ? [required] : required;
  if (keys.length === 0) return true;
  if (isSuperAdminUser(user)) return true;
  return keys.some((key) => permissions.has(key));
}

/**
 * Where this account belongs after sign-in.
 *
 * `bestallare` (restaurant orderer) is a PORTAL-only role: its permission set
 * has no warehouse or admin surface at all, so dropping it on the admin
 * dashboard would show an empty shell. Anyone holding any other role keeps the
 * admin app — a restaurant manager who is also `lager` is a real case.
 */
export function homePathFor(user: SessionUser | null): string {
  if (!user) return '/login';
  if (isSuperAdminUser(user)) return '/';
  const roles = user.roleKeys;
  const portalOnly = roles.length > 0 && roles.every((role) => role === 'bestallare');
  return portalOnly ? '/portal' : '/';
}

// ---------------------------------------------------------------------------
// hooks
// ---------------------------------------------------------------------------

export function useAuthUser(): SessionUser | null {
  return useAuthStore((s) => s.user);
}

export function useAuthStatus(): AuthStatus {
  return useAuthStore((s) => s.status);
}

/** `usePermission('warehouse.map', 'warehouse.map.manage')` — OR semantics. */
export function usePermission(...required: PermissionKey[]): boolean {
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  return userHasPermission(user, permissions, required);
}

/** A stable predicate for filtering lists of nav items / actions in one pass. */
export function usePermissionCheck(): (required?: readonly PermissionKey[]) => boolean {
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  return (required) => userHasPermission(user, permissions, required);
}
